import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Loader2, Save, Search, Send, X } from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import {
  createEmailDraft,
  getActiveEmailSenderProfiles,
  getActiveEmailTemplates,
  sendEmailNow,
  sendEmailDraft,
  updateEmailMessage,
} from '../../Services/EmailAdminService';
import { EmailMessage, EmailSenderProfile, EmailTemplate } from '../../Types/EmailAdminType';
import EmailRecipientPicker, { EmailRecipientPickerRef } from './EmailRecipientPicker';

interface Props {
  open: boolean;
  draft: EmailMessage | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

/**
 * Gmail-style composer. The same form drives a fresh message, a re-opened draft and a re-send:
 * the only difference is which service call runs on Save / Send.
 */
function EmailComposeModal({ open, draft, onClose, onDone }: Props) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string>(draft?.templateId ?? '');
  const [templateQuery, setTemplateQuery] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  const [profiles, setProfiles] = useState<EmailSenderProfile[]>([]);
  const [senderProfileId, setSenderProfileId] = useState<string>('');

  const [fromEmail, setFromEmail] = useState(draft?.fromEmail ?? '');
  const [fromName, setFromName] = useState(draft?.fromName ?? '');
  const [to, setTo] = useState<string[]>(draft?.toEmails ?? []);
  const [cc, setCc] = useState<string[]>(draft?.ccEmails ?? []);
  const [bcc, setBcc] = useState<string[]>(draft?.bccEmails ?? []);
  const [showCc, setShowCc] = useState((draft?.ccEmails?.length ?? 0) > 0);
  const [showBcc, setShowBcc] = useState((draft?.bccEmails?.length ?? 0) > 0);
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [body, setBody] = useState(draft?.body ?? '');
  const [busy, setBusy] = useState<'send' | 'draft' | null>(null);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const toPickerRef = useRef<EmailRecipientPickerRef>(null);

  const savedId = draft?.id;

  useEffect(() => {
    if (!open) return;
    getActiveEmailTemplates()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) setTemplates(res.data);
      })
      .catch(() => undefined);
    getActiveEmailSenderProfiles()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) setProfiles(res.data);
      })
      .catch(() => undefined);
  }, [open]);

  // Close the template dropdown when the user clicks outside of it.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (templateRef.current && !templateRef.current.contains(e.target as Node)) {
        setTemplateOpen(false);
      }
    }
    if (templateOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [templateOpen]);

  // Pre-select a sender profile when a draft's From matches a configured profile.
  useEffect(() => {
    if (!open || profiles.length === 0) return;
    if (draft?.fromEmail && draft?.fromName) {
      const match = profiles.find(
        (p) => p.fromEmail === draft.fromEmail && p.fromName === draft.fromName
      );
      if (match) setSenderProfileId(match.id);
    }
  }, [open, profiles, draft?.fromEmail, draft?.fromName]);

  // Keep the template search field in sync with the selected template.
  useEffect(() => {
    const selected = templates.find((t) => t.id === templateId);
    setTemplateQuery(selected ? selected.name : '');
  }, [templateId, templates]);

  // When a draft is reopened, copy the selected template's subject/body into the composer if
  // the fields are currently empty. This mirrors what applyTemplate does on manual selection.
  useEffect(() => {
    if (!open || !draft?.templateId || templates.length === 0) return;
    const t = templates.find((x) => x.id === draft.templateId);
    if (!t) return;
    setSubject((prev) => prev || t.subject || '');
    setBody((prev) => prev || t.htmlBody || '');
  }, [open, draft?.templateId, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId]
  );

  const effectiveSubject = useMemo(
    () => subject.trim() || selectedTemplate?.subject || '',
    [subject, selectedTemplate]
  );

  const effectiveBody = useMemo(
    () => body.trim() || selectedTemplate?.htmlBody || '',
    [body, selectedTemplate]
  );

  const canSend = useMemo(
    () => to.length > 0 && effectiveSubject.length > 0 && effectiveBody.length > 0,
    [to, effectiveSubject, effectiveBody]
  );

  const filteredTemplates = useMemo(() => {
    const q = templateQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.subject ?? '').toLowerCase().includes(q) ||
        (t.category ?? '').toLowerCase().includes(q)
    );
  }, [templates, templateQuery]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    setTemplateOpen(false);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    if (t.subject) setSubject(t.subject);
    if (t.htmlBody) setBody(t.htmlBody);
  };

  const applySenderProfile = (id: string) => {
    setSenderProfileId(id);
    const p = profiles.find((x) => x.id === id);
    if (!p) {
      setFromEmail('');
      setFromName('');
      return;
    }
    setFromEmail(p.fromEmail);
    setFromName(p.fromName);
  };

  const buildPayload = () => ({
    fromEmail: fromEmail.trim() || undefined,
    fromName: fromName.trim() || undefined,
    toEmails: to,
    ccEmails: cc,
    bccEmails: bcc,
    subject: effectiveSubject,
    body: effectiveBody,
    templateId: templateId || undefined,
  });

  const handleSaveDraft = async () => {
    setBusy('draft');
    try {
      const res = savedId
        ? await updateEmailMessage(savedId, buildPayload())
        : await createEmailDraft(buildPayload());
      if (res.success) {
        onDone('Draft saved.');
        onClose();
      } else {
        setAlert({
          variant: 'error',
          title: 'Save failed',
          message: res.message || 'Please try again.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } finally {
      setBusy(null);
    }
  };

  const handleSend = async () => {
    // Flush any e-mail address the user typed but did not explicitly commit.
    const committedTo = toPickerRef.current?.commit() ?? to;
    const missing: string[] = [];
    if (committedTo.length === 0) missing.push('at least one recipient');
    if (!effectiveSubject) missing.push('a subject');
    if (!effectiveBody) missing.push('a body');
    if (missing.length > 0) {
      setAlert({
        variant: 'error',
        title: 'Incomplete',
        message: `Please add ${missing.join(', ')} before sending.`,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }
    setBusy('send');
    try {
      const payload = { ...buildPayload(), toEmails: committedTo };
      let res;
      if (savedId) {
        // Persist the latest edits on the draft, then dispatch it.
        await updateEmailMessage(savedId, payload);
        res = await sendEmailDraft(savedId);
      } else {
        res = await sendEmailNow(payload);
      }
      if (res.success) {
        onDone('Email sent.');
        onClose();
      } else {
        setAlert({
          variant: 'error',
          title: 'Send failed',
          message: res.message || 'The provider rejected the message.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9950] flex items-start justify-center overflow-y-auto bg-slate-950/80 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="mt-6 w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
          <h2 className="text-sm font-black text-slate-100">{savedId ? 'Edit Draft' : 'New Message'}</h2>
          <button
            onClick={onClose}
            className="ml-auto rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelClass}>Sender profile</label>
            <div className="relative">
              <select
                value={senderProfileId}
                onChange={(e) => applySenderProfile(e.target.value)}
                className={`${fieldClass} appearance-none pr-9`}
              >
                <option value="">System default sender</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fromName} &lt;{p.fromEmail}&gt;{p.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>
            <p className="mt-1 text-[10px] font-semibold text-slate-600">
              Choose a configured profile or enter a custom From address below.
            </p>
          </div>

          <div>
            <label className={labelClass}>From email</label>
            <input
              className={fieldClass}
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="Default sender if blank"
            />
          </div>
          <div>
            <label className={labelClass}>From name</label>
            <input
              className={fieldClass}
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Default sender name if blank"
            />
          </div>
        </div>

        <div className="space-y-3 px-4 pb-2">
          <div>
            <label className={labelClass}>To</label>
            <EmailRecipientPicker ref={toPickerRef} value={to} onChange={setTo} placeholder="Add recipients…" />
          </div>

          <div className="flex gap-3 text-[11px] font-black uppercase tracking-widest">
            {!showCc && (
              <button
                type="button"
                onClick={() => setShowCc(true)}
                className="text-slate-500 transition hover:text-cyan-400"
              >
                + Cc
              </button>
            )}
            {!showBcc && (
              <button
                type="button"
                onClick={() => setShowBcc(true)}
                className="text-slate-500 transition hover:text-cyan-400"
              >
                + Bcc
              </button>
            )}
          </div>

          {showCc && (
            <div>
              <label className={labelClass}>Cc</label>
              <EmailRecipientPicker value={cc} onChange={setCc} placeholder="Add Cc…" />
            </div>
          )}
          {showBcc && (
            <div>
              <label className={labelClass}>Bcc</label>
              <EmailRecipientPicker value={bcc} onChange={setBcc} placeholder="Add Bcc…" />
            </div>
          )}

          <div ref={templateRef} className="relative">
            <label className={labelClass}>Template (optional)</label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={templateQuery}
                onChange={(e) => {
                  setTemplateQuery(e.target.value);
                  setTemplateOpen(true);
                }}
                onFocus={() => setTemplateOpen(true)}
                placeholder="Search templates…"
                className={`${fieldClass} pl-9 pr-9`}
              />
              {templateId && (
                <button
                  type="button"
                  onClick={() => {
                    setTemplateId('');
                    setTemplateQuery('');
                  }}
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-300"
                  aria-label="Clear template"
                >
                  <X size={14} />
                </button>
              )}
              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
            </div>
            {templateOpen && (
              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-slate-800 bg-slate-900 shadow-xl">
                <button
                  type="button"
                  onClick={() => {
                    setTemplateId('');
                    setTemplateQuery('');
                    setTemplateOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm font-semibold text-slate-400 transition hover:bg-slate-800"
                >
                  No template
                </button>
                {filteredTemplates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => applyTemplate(t.id)}
                    className="w-full px-3 py-2 text-left text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
                  >
                    <span className="block">{t.name}</span>
                    {t.subject && (
                      <span className="block truncate text-[11px] font-medium text-slate-500">
                        {t.subject}
                      </span>
                    )}
                  </button>
                ))}
                {filteredTemplates.length === 0 && (
                  <div className="px-3 py-2 text-sm font-semibold text-slate-600">
                    No templates match
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Subject</label>
            <input
              className={fieldClass}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line"
            />
          </div>

          <div>
            <label className={labelClass}>Body (HTML)</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              spellCheck={false}
              className="min-h-[220px] w-full resize-y rounded-xl border border-slate-800 bg-slate-950/80 p-3 font-mono text-xs leading-relaxed text-slate-200 outline-none transition focus:border-cyan-500/50"
              placeholder="<p>Hello…</p>"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-800 px-4 py-3">
          <button
            onClick={handleSend}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2.5 text-sm font-black text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:brightness-110 disabled:opacity-50"
          >
            {busy === 'send' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Send
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-sm font-black text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-50"
          >
            {busy === 'draft' ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Draft
          </button>
          <span className="ml-auto text-[10px] font-semibold text-slate-600">
            Choose a sender profile or leave From blank to use the configured default sender
          </span>
        </div>
      </motion.div>

      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}
    </div>
  );
}

export default EmailComposeModal;
